import type { SxProps, Theme } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  alpha,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { useRouter } from "next/router";
import { useCallback, useContext, useMemo, useState } from "react";

import SafeImage from "@/components/common/SafeImage";
import {
  APPBAR_HEIGHT,
  PAGE_PADDING,
  gutterFor,
} from "@/constants/layout";
import useNavActions, { type NavAction } from "@/components/menu/useNavActions";
import { ORDER_TYPE_ENUM } from "@/constants/enums";
import { MODAL_VIEW } from "@/constants/views";
import { Z_INDEX } from "@/constants/zIndex";
import { CartDrawerContext } from "@/context/CartDrawerContext";
import { useBranch } from "@/context/BranchContext";
import useDesignType from "@/hooks/useDesignType";
import { useAppDispatch, useAppSelector } from "@/store";
import { openModal } from "@/store/slices/commonSlice";

/**
 * The menu page's top bar, rebuilt from the original's `HomeNavbar`.
 *
 * The reference carries no back arrow, no page title and no cart button: just
 * the brand logo, the search pill (desktop only) and a right-aligned group of
 * action chips — `Offers`, `Reservations`, and either the customer's own name
 * or `Login`. Below the `lg` breakpoint the original collapses that group to
 * its first visible entry and moves the rest onto the bottom order bar, which
 * is what `showFirstOptionOnly` does here.
 */

/* --------------------------------- shell ---------------------------------- */

// `Box`, not `Stack`: the bar is a three-column GRID now (see below), and
// `Stack`'s own `display: flex` would race this rule for the same specificity.
const Bar = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "hidden" &&
    prop !== "isModernDesign" &&
    prop !== "isVibrantDesign",
})<{
  hidden?: boolean;
  isModernDesign?: boolean;
  isVibrantDesign?: boolean;
}>(({ theme, hidden, isModernDesign, isVibrantDesign }) => {
  const gutter = gutterFor(isModernDesign);
  return {
    position: "sticky",
    zIndex: Z_INDEX.NAVBAR,
    // The original parks the bar just off the top of the scroller and slides
    // it back on the first upward scroll (`top: -60` ⇄ `top: 0`, 0.6s).
    top: hidden ? -APPBAR_HEIGHT : 0,
    transition: "top 0.6s ease-in-out",
    // `...(isVibrantDesign ? {} : { [up("md")]: { top: 0 } })` — every design
    // pins the bar from `md` up EXCEPT the VIBRANT pair, which keeps
    // auto-hiding on the desktop too. Reproduced, not tidied.
    ...(isVibrantDesign ? {} : { [theme.breakpoints.up("md")]: { top: 0 } }),
    left: 0,
    right: 0,
    height: APPBAR_HEIGHT,
    minHeight: APPBAR_HEIGHT,
    maxHeight: APPBAR_HEIGHT,
    padding: `0 ${PAGE_PADDING}px`,
    backgroundColor: theme.palette.background.default,
    // The reference bar's hairline rule. It is what separates the bar from the
    // sticky pill row directly beneath it, which is otherwise the same colour.
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
    /**
     * THREE COLUMNS, and the middle one is why.
     *
     * The reference centres its logo in the BAR, not between its neighbours —
     * and the two flanks are different widths (a two-line location block on the
     * left, an icon cluster on the right), so a flex row with
     * `justifyContent: space-between` would push the logo off-centre by half
     * their difference. `1fr auto 1fr` pins the middle track to the bar's own
     * centre line whatever the flanks do.
     */
    display: "grid",
    /**
     * `minmax(0, 1fr)`, not a bare `1fr`. A bare `1fr` has an implicit
     * `min-width: auto`, so a track refuses to shrink below its content — at
     * 360px the location block and the icon cluster together are wider than
     * the bar, and the row would push the page into a horizontal scroll. With
     * an explicit `0` minimum both flanks shrink, the location line truncates
     * (it is `noWrap`), and the logo still sits on the bar's centre line
     * because the two flanking tracks stay equal.
     */
    gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
    alignItems: "center",
    columnGap: theme.spacing(0.5),
    [theme.breakpoints.up("sm")]: { columnGap: theme.spacing(1) },
    [theme.breakpoints.up("lg")]: { padding: `0 ${gutter.lg}` },
    [theme.breakpoints.up("xl")]: { padding: `0 ${gutter.xl}` },
    [theme.breakpoints.up("xxl")]: { padding: `0 ${gutter.xxl}` },
    [theme.breakpoints.up("xxxl")]: { padding: `0 ${gutter.xxxl}` },
  };
});

/** 44px on a phone, the original's 55 from `sm` up — see `Bar`'s width sums. */
const LOGO_SIZE_MOBILE = 44;
const LOGO_SIZE = APPBAR_HEIGHT - 5;

const LogoFrame = styled(Stack)(({ theme }) => ({
  position: "relative",
  height: LOGO_SIZE_MOBILE,
  [theme.breakpoints.up("sm")]: { height: LOGO_SIZE },
}));

const LogoCircle = styled(Box)(({ theme }) => ({
  position: "relative",
  height: LOGO_SIZE_MOBILE,
  width: LOGO_SIZE_MOBILE,
  minWidth: LOGO_SIZE_MOBILE,
  maxWidth: LOGO_SIZE_MOBILE,
  borderRadius: "50%",
  overflow: "hidden",
  img: { objectFit: "cover", objectPosition: "center" },
  [theme.breakpoints.up("sm")]: {
    height: LOGO_SIZE,
    width: LOGO_SIZE,
    minWidth: LOGO_SIZE,
    maxWidth: LOGO_SIZE,
  },
}));

export function BrandLogo({
  logoUrl,
  sx,
}: {
  logoUrl?: string | null;
  sx?: SxProps<Theme>;
}) {
  if (!logoUrl) return <LogoFrame sx={{ alignItems: "center", ...sx }} />;
  return (
    <LogoFrame sx={{ alignItems: "center", ...sx }}>
      <LogoCircle>
        <SafeImage
          src={logoUrl}
          alt="logo"
          placeholder="logo"
          style={{ width: "100%", height: "100%" }}
        />
      </LogoCircle>
    </LogoFrame>
  );
}

/* ------------------------------- search pill ------------------------------ */

// The original animates the placeholder a character at a time, cycling
// "Search Menu..." and "Find Items...", via a `content` keyframe on ::before.
const typewriter = keyframes`
  0% { content: "S"; }
  2% { content: "Se"; }
  4% { content: "Sea"; }
  6% { content: "Sear"; }
  8% { content: "Searc"; }
  10% { content: "Search"; }
  12% { content: "Search M"; }
  14% { content: "Search Me"; }
  16% { content: "Search Men"; }
  18% { content: "Search Menu"; }
  20% { content: "Search Menu."; }
  22% { content: "Search Menu.."; }
  24% { content: "Search Menu..."; }
  26%, 30% { content: "Search Menu"; }

  32% { content: "Search Men"; }
  34% { content: "Search Me"; }
  36% { content: "Search M"; }
  38% { content: "Search"; }
  40% { content: "Searc"; }
  42% { content: "Sear"; }
  44% { content: "Sea"; }
  46% { content: "Se"; }
  48% { content: "S"; }
  50% { content: ""; }

  52% { content: "F"; }
  54% { content: "Fi"; }
  56% { content: "Fin"; }
  58% { content: "Find"; }
  60% { content: "Find I"; }
  62% { content: "Find It"; }
  64% { content: "Find Ite"; }
  66% { content: "Find Item"; }
  68% { content: "Find Items"; }
  70% { content: "Find Items."; }
  72% { content: "Find Items.."; }
  74% { content: "Find Items..."; }
  76%, 80% { content: "Find Items"; }

  82% { content: "Find Item"; }
  84% { content: "Find Ite"; }
  86% { content: "Find It"; }
  88% { content: "Find I"; }
  90% { content: "Find"; }
  92% { content: "Fin"; }
  94% { content: "Fi"; }
  96% { content: "F"; }
  98% { content: ""; }
  100% { content: "S"; }
`;

const SearchPill = styled(Stack)(({ theme }) => ({
  boxShadow: `0 4px 20px ${theme.palette.mode === "dark" ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)"}`,
  maxWidth: 250,
  width: "100%",
  height: 42,
  borderRadius: 50,
  padding: "8px 16px",
  backgroundColor: theme.palette.background.default,
  border: `1.5px solid ${theme.palette.divider}`,
  transition: theme.transitions.create(["all"], { duration: 300 }),
  display: "flex",
  alignItems: "center",
  gap: "12px",
  cursor: "pointer",
  "&:hover": {
    boxShadow: `0 8px 30px ${theme.palette.mode === "dark" ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.15)"}`,
    borderColor: theme.palette.primary.main,
    transform: "translateY(-2px)",
  },
  "&:active": { transform: "translateY(0)" },
}));

const SearchIconCircle = styled(Stack)(({ theme }) => ({
  width: 25,
  height: 25,
  borderRadius: "50%",
  alignItems: "center",
  justifyContent: "center",
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
  transition: theme.transitions.create(["all"], { duration: 300 }),
  color: theme.palette.primary.contrastText,
  "&:hover": {
    transform: "scale(1.1)",
    boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
  },
}));

const SearchLabel = styled(Typography)(({ theme }) => ({
  flex: 1,
  fontWeight: 500,
  letterSpacing: "0.3px",
  minHeight: "24px",
  display: "flex",
  alignItems: "center",
  transition: theme.transitions.create(["color"], { duration: 300 }),
  minWidth: "120px",
  whiteSpace: "nowrap",
  "&::before": {
    content: '"S"',
    animation: `${typewriter} 12s ease-in-out infinite`,
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&::before": { content: '"Search Menu"', animation: "none" },
  },
}));

const ActionChip = styled(Chip)(({ theme }) => ({
  boxShadow: theme.shadows[2],
  border: "none",
}));

/** `tP` / `nu` — both navbars style the Staff chip identically. */
const StaffChipRoot = styled(Chip)({ zIndex: Z_INDEX.BASE });

/**
 * The **Staff** chip — §8 of `recovery-source/SPEC_staff-login.md`.
 *
 * ```js
 * r && p && null == a && <tP label="Staff" variant="outlined" color="primary"
 *   size="small" icon={<PersonPinCircle/>} onClick={() => d($U(KL))} />
 * ```
 *
 * `r` is `showStaffIcon`, which BOTH real call sites pass as `true`; `p` is
 * `auth.isStaff`; `a` is the navbar's `children`, and neither navbar in this
 * tree takes any. So the live condition is `isStaff`, and a customer never
 * sees it.
 *
 * It is the ONLY way into `STAFF_INFO_MODAL_JOIN`, which means it is the only
 * route to "Switch Staff".
 */
export function StaffChip() {
  const dispatch = useAppDispatch();
  const isStaff = useAppSelector((state) => state.auth.isStaff);
  if (!isStaff) return null;
  return (
    <StaffChipRoot
      label="Staff"
      variant="outlined"
      color="primary"
      size="small"
      icon={<PersonPinCircleIcon />}
      onClick={() => dispatch(openModal(MODAL_VIEW.STAFF_INFO_JOIN))}
    />
  );
}

/* -------------------------------- actions --------------------------------- */

export interface HomeNavbarProps {
  /** Collapse the action group to its first visible entry (mobile). */
  showFirstOptionOnly?: boolean;
  /** Last scroll direction of the menu column; "down" hides the bar. */
  scrollDirection?: "up" | "down";
  /** `settings.web_design_type` starts with MODERN — widens the `xxxl` gutter. */
  isModernDesign?: boolean;
  /** ends with VIBRANT — the bar keeps auto-hiding above `md`. */
  isVibrantDesign?: boolean;
}

/**
 * The account dropdown — the identity block plus the entry list.
 *
 * Extracted from `NavActions` because the restructured header's circular
 * account button opens the SAME menu. Two copies of it is how the chip group
 * and the icon cluster would drift apart, and this menu is where Logout lives.
 */
function AccountMenu({
  entry,
  anchor,
  onClose,
}: {
  entry: NavAction;
  anchor: HTMLElement | null;
  onClose: () => void;
}) {
  return (
    <Menu
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={onClose}
      sx={{ zIndex: Z_INDEX.POPUP }}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: (theme) => ({
            mt: 1,
            minWidth: 252,
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 12px 40px rgba(0,0,0,0.5)"
                : "0 12px 40px rgba(0,0,0,0.14)",
            "& .MuiList-root": { padding: theme.spacing(0.75) },
          }),
        },
      }}
    >
      {/* The identity block. Rendered as a plain Box, NOT a MenuItem, so the
          keyboard never lands on it and a stray tap cannot dismiss the menu. */}
      {entry.menuHeader && (
        <Box sx={{ px: 1.25, pt: 1.25, pb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={(theme) => ({
                width: 38,
                height: 38,
                fontSize: 15,
                fontWeight: 700,
                color: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
              })}
            >
              {entry.menuHeader.title.trim().charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {entry.menuHeader.title}
              </Typography>
              {entry.menuHeader.subtitle && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {entry.menuHeader.subtitle}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      )}
      {(entry.menuItems ?? []).map((item) => [
        // A destructive entry is separated from the ordinary ones, so Logout is
        // never the thing a fast tap lands on by accident.
        item.danger ? (
          <Divider key={item.label + "-rule"} sx={{ my: 0.75 }} />
        ) : null,
        <MenuItem
          key={item.label}
          onClick={() => {
            item.onClick();
            onClose();
          }}
          sx={(theme) => ({
            borderRadius: 2,
            px: 1.25,
            py: 1,
            gap: 0.5,
            color: item.danger ? theme.palette.error.main : undefined,
            "& .MuiListItemIcon-root": {
              minWidth: 34,
              color: item.danger
                ? theme.palette.error.main
                : theme.palette.text.secondary,
            },
            "&:hover": {
              backgroundColor: alpha(
                item.danger
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
                0.1,
              ),
            },
          })}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}
          >
            {item.label}
          </ListItemText>
        </MenuItem>,
      ])}
    </Menu>
  );
}

/**
 * `to` — the right-hand chip group, shared by BOTH navbars (`tE` and `nd`
 * render the identical element). Exported so `SlateHomeNavbar` does not
 * duplicate the action list, which is where an inconsistency would creep in.
 */
export function NavActions({
  showFirstOptionOnly,
}: {
  showFirstOptionOnly?: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const closeMenu = () => {
    setAnchor(null);
    setOpenIndex(null);
  };

  // `Y` — the shared action list. The mobile order bar's SpeedDial renders the
  // SAME entries, so it must not be rebuilt here.
  const actions = useNavActions();

  const visible = useMemo(() => {
    if (!showFirstOptionOnly) return actions.filter((entry) => !entry.hide);
    const first = actions.find((entry) => !entry.hide);
    return first ? [first] : [];
  }, [actions, showFirstOptionOnly]);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      spacing={1}
    >
      {visible.map((entry, index) => (
          <div key={entry.label}>
            <ActionChip
              variant="filled"
              color={entry.color}
              icon={entry.icon as never}
              label={entry.label}
              onClick={
                entry.menuItems
                  ? (event) => {
                      setAnchor(event.currentTarget);
                      setOpenIndex(index);
                    }
                  : entry.onClick
              }
            />
            {entry.menuItems && (
              <AccountMenu
                entry={entry}
                anchor={openIndex === index ? anchor : null}
                onClose={closeMenu}
              />
            )}
        </div>
      ))}
    </Stack>
  );
}

/**
 * `tv` — the animated search pill. Desktop only in both navbars.
 */
export function NavSearchPill() {
  const dispatch = useAppDispatch();
  const open = () => dispatch(openModal(MODAL_VIEW.SEARCH_ITEMS));
  return (
    <SearchPill
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      role="button"
      tabIndex={0}
      aria-label="Search the menu"
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          open();
        }
      }}
    >
      <SearchLabel variant="body2" color="text.secondary" />
      <SearchIconCircle>
        <SearchIcon fontSize="small" />
      </SearchIconCircle>
    </SearchPill>
  );
}

/**
 * The MOBILE search affordance.
 *
 * The animated pill needs ~250px of width, so both navbars gate it behind
 * `md` — which left phones with no route into the menu search at all, even
 * though `SearchItemsModal` is mounted globally and works fine. This is the
 * same action as a 38px circular button, reusing the pill's gradient icon
 * circle so the two read as one feature rather than two.
 */
const SearchButton = styled(IconButton)(({ theme }) => ({
  // Matched to `ClusterButton`'s ladder so the three header controls are one
  // row of equal circles rather than 38/42/42.
  width: 36,
  height: 36,
  [theme.breakpoints.up("sm")]: { width: 42, height: 42 },
  flexShrink: 0,
  color: theme.palette.primary.contrastText,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
  transition: theme.transitions.create(["all"], { duration: 300 }),
  "&:hover": {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    boxShadow: `0 6px 20px ${theme.palette.primary.main}60`,
  },
  "&:active": { transform: "scale(0.94)" },
}));

export function NavSearchButton() {
  const dispatch = useAppDispatch();
  return (
    <SearchButton
      aria-label="Search the menu"
      onClick={() => dispatch(openModal(MODAL_VIEW.SEARCH_ITEMS))}
    >
      <SearchIcon fontSize="small" />
    </SearchButton>
  );
}

/* ---------------------------- the location line ---------------------------- */

/**
 * `HeaderLocation` — the reference's left-hand block: a map pin beside two
 * stacked lines, "Delivery to ⌄" over the place itself.
 *
 * WHY IT IS IN THE HEADER AT ALL. The branch decides prices, delivery areas and
 * stock, and until now the only way to see or change it was the overflow menu
 * (`AppMenu`) — a customer could be on the wrong outlet for a whole session and
 * never know. Putting it on the bar makes the single most consequential piece
 * of state on this site permanently visible, which is exactly what the
 * reference does with it.
 *
 * WHAT THE SECOND LINE SAYS, in order of what is actually known:
 *
 *   1. the delivery address the customer has entered, when this is a delivery
 *      basket and they have entered one — that is the literal answer to
 *      "delivery to";
 *   2. otherwise the branch's own street address or city;
 *   3. otherwise the branch's name, which always exists.
 *
 * …followed by the tenant's own quoted time for the current order type
 * (`delivery_time` / `takeaway_time`, milliseconds on the settings record). It
 * is REAL configured data — this tenant quotes 210 minutes for delivery — and
 * it is rendered as configured rather than massaged into something friendlier.
 *
 * The whole block is one button. Where it goes depends on what there is to
 * change: a multi-outlet brand opens the branch picker (the consequential
 * choice), a single-outlet one opens the delivery-address modal.
 */
const LocationButton = styled("button")(({ theme }) => ({
  appearance: "none",
  background: "none",
  border: "none",
  padding: theme.spacing(0.5, 0.75),
  margin: theme.spacing(0, -0.75),
  borderRadius: 12,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  minWidth: 0,
  maxWidth: "100%",
  textAlign: "left",
  color: "inherit",
  // 44px of vertical target inside a 60px bar, per the touch-target floor.
  minHeight: 44,
  transition: theme.transitions.create(["background-color"]),
  "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.06) },
}));

const LocationLines = styled(Box)({
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  lineHeight: 1.15,
});

/** ms → "50 min" / "3 hr 30 min". Presentation only; the value is the tenant's. */
const formatEta = (milliseconds?: number | null): string | null => {
  if (!milliseconds || !Number.isFinite(milliseconds) || milliseconds <= 0)
    return null;
  const minutes = Math.round(milliseconds / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
};

export function HeaderLocation() {
  const dispatch = useAppDispatch();
  const { branches, current, openPicker } = useBranch();
  const settings = useAppSelector((state) => state.settings.data);
  const orderType = useAppSelector((state) => state.cart.orderType);
  const address = useAppSelector((state) => state.cart.address);

  const isDelivery = orderType === ORDER_TYPE_ENUM.DELIVERY;
  const isDineIn = orderType === ORDER_TYPE_ENUM.DINE_IN;
  const multiBranch = branches.length > 1;

  const label = isDelivery
    ? "Delivery to"
    : isDineIn
      ? "Dine-in at"
      : settings.takeaway_is_pickup
        ? "Pickup from"
        : "Takeaway from";

  const place =
    (isDelivery &&
      (address?.line1?.trim() ||
        [address?.areaName, address?.city].filter(Boolean).join(", "))) ||
    current?.restaurant_address?.trim() ||
    settings.restaurant_address?.trim() ||
    current?.city?.trim() ||
    settings.city?.trim() ||
    current?.name ||
    settings.public_business_name ||
    "";

  const eta = formatEta(
    isDelivery ? settings.delivery_time : settings.takeaway_time,
  );

  // Nothing known and nothing to change — render nothing rather than an empty
  // pin. The grid's `1fr` track simply stays empty and the logo stays centred.
  if (!place && !multiBranch) return null;

  return (
    <LocationButton
      type="button"
      aria-label={
        multiBranch
          ? `${label} ${place}. Change branch`
          : `${label} ${place}. Change delivery address`
      }
      onClick={() =>
        multiBranch
          ? openPicker()
          : dispatch(openModal(MODAL_VIEW.DELIVERY_INFO))
      }
    >
      <PlaceOutlinedIcon
        fontSize="small"
        color="primary"
        sx={{ flex: "0 0 auto" }}
      />
      <LocationLines>
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
            {label}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{ fontSize: 14, color: "text.secondary", flex: "0 0 auto" }}
          />
        </Stack>
        <Typography
          variant="caption"
          fontWeight={600}
          noWrap
          lineHeight={1.2}
          sx={{ maxWidth: { xxs: 100, sm: 220, lg: 300 } }}
        >
          {place}
          {eta ? ` ~ ${eta}` : ""}
        </Typography>
      </LocationLines>
    </LocationButton>
  );
}

/* ---------------------------- the icon cluster ----------------------------- */

/**
 * `HeaderActions` — the reference's right-hand group: search, account, cart.
 *
 * RECONCILING THE TWO SEARCH AFFORDANCES. This bar used to carry the animated
 * `NavSearchPill` above `md` and the round `NavSearchButton` below it. The
 * reference has ONE search control at every width, and the pill needs 250px
 * that the centred logo now wants, so the round button is the only one here.
 * Both components are still exported and still used verbatim by
 * `SlateHomeNavbar`, which is a different design type and is not being
 * restyled.
 *
 * THE OTHER NAV ACTIONS. `useNavActions` is the single source for Offers,
 * Reservations, Floor Plan, the account menu and Login, and the mobile order
 * bar's SpeedDial renders the SAME list. Rendering them as icons rather than
 * labelled chips is presentation only — the list, its gating and its handlers
 * are untouched. Below `md` only the account/login entry is kept on the bar;
 * everything else stays one tap away on the order bar, which is where it
 * already was.
 */
const ClusterButton = styled(IconButton)(({ theme }) => ({
  // 36px at the very small end so the three controls, the logo and the
  // location block all fit inside 360px without a horizontal scrollbar; 42
  // from `sm` up. The whole cluster row is 44px tall regardless, so the tap
  // target still clears the floor vertically.
  width: 36,
  height: 36,
  flex: "0 0 auto",
  [theme.breakpoints.up("sm")]: { width: 42, height: 42 },
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
  backgroundColor: theme.palette.background.default,
  transition: theme.transitions.create(["background-color", "border-color"]),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    borderColor: alpha(theme.palette.primary.main, 0.4),
  },
}));

/** The account control gets the reference's filled circular treatment. */
const AccountButton = styled(ClusterButton)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  borderColor: theme.palette.primary.main,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    borderColor: theme.palette.primary.dark,
  },
}));

export function HeaderActions() {
  const dispatch = useAppDispatch();
  const actions = useNavActions();
  const theme = useTheme();
  const isLargerThanMobile = useMediaQuery(theme.breakpoints.up("md"));
  const { isTemporary } = useContext(CartDrawerContext);
  const navRouter = useRouter();
  // Same rule as the order bar: the basket opens the checkout directly. The
  // summary drawer it used to open only led there. See OrderBar's openOrder.
  const openBasket = useCallback(() => {
    void navRouter.push("/review-order");
  }, [navRouter]);
  const { isModernDesign } = useDesignType();
  const cartLines = useAppSelector((state) => state.cart.items);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const visible = actions.filter((entry) => !entry.hide);
  // The account entry is the one carrying a dropdown; Login is the signed-out
  // stand-in for it. Identified by shape rather than by label so a copy change
  // cannot silently drop the control.
  const account = visible.find((entry) => entry.menuItems || entry.label === "Login");
  const others = visible.filter((entry) => entry !== account);

  const cartCount = cartLines.reduce((sum, line) => sum + line.quantity, 0);

  /**
   * Under `CLASSIC*` from `lg` up the basket is a PERMANENT panel already on
   * screen (see `CartPanel`'s variant table), so a header cart button would
   * open something that is not closed. It is rendered only when the basket is
   * actually a drawer — which is every phone, every tablet, and every
   * `MODERN*` width.
   */
  const cartIsDrawer = isTemporary || isModernDesign;

  const iconOf = (entry: NavAction) => entry.icon;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      spacing={{ xxs: 0.75, sm: 1 }}
    >
      {/* Search is a ClusterButton rather than the exported `NavSearchButton`
          on purpose: that one carries the brand GRADIENT fill, and next to the
          filled account circle two solid brand discs read as two primary
          actions. The reference has exactly one filled control in the cluster —
          the account — so search and basket are outlined. `NavSearchButton`
          stays exported and unchanged for `SlateHomeNavbar`; both dispatch the
          same `SEARCH_ITEMS` modal, so there is one search feature, not two. */}
      <Tooltip title="Search the menu">
        <ClusterButton
          aria-label="Search the menu"
          onClick={() => dispatch(openModal(MODAL_VIEW.SEARCH_ITEMS))}
        >
          <SearchIcon />
        </ClusterButton>
      </Tooltip>

      {isLargerThanMobile &&
        others.map((entry) => (
          <Tooltip key={entry.label} title={entry.label}>
            <ClusterButton aria-label={entry.label} onClick={entry.onClick}>
              {iconOf(entry)}
            </ClusterButton>
          </Tooltip>
        ))}

      {account && (
        <>
          <Tooltip title={account.label}>
            <AccountButton
              aria-label={account.label}
              onClick={(event) =>
                account.menuItems
                  ? setAnchor(event.currentTarget)
                  : account.onClick?.()
              }
            >
              {iconOf(account)}
            </AccountButton>
          </Tooltip>
          {account.menuItems && (
            <AccountMenu
              entry={account}
              anchor={anchor}
              onClose={() => setAnchor(null)}
            />
          )}
        </>
      )}

      {cartIsDrawer && (
        <Tooltip title="Your basket">
          <ClusterButton aria-label="Your basket" onClick={openBasket}>
            <Badge
              badgeContent={cartCount}
              color="primary"
              showZero
              overlap="circular"
            >
              <ShoppingBagOutlinedIcon />
            </Badge>
          </ClusterButton>
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * `tE` — the standard 60px navbar, restructured to the reference's three-part
 * bar: the location block on the left, the brand logo centred, the icon
 * cluster on the right. Used by every design type EXCEPT the two that carry
 * `SLATE`, which get `SlateHomeNavbar` (`nd`) instead.
 *
 * `showFirstOptionOnly` is no longer read: the cluster decides for itself which
 * entries survive the small end (see `HeaderActions`). The prop is kept on the
 * interface because `SlateHomeNavbar` still takes and uses it.
 */
import OrderTypeMenu from "./OrderTypeMenu";
import StoreRating from "./StoreRating";

export default function HomeNavbar({
  scrollDirection = "up",
  isModernDesign,
  isVibrantDesign,
}: HomeNavbarProps) {
  const logoUrl = useAppSelector((state) => state.settings.data.image_url);

  return (
    <Bar
      className={scrollDirection === "up" ? "main-header up" : "main-header"}
      hidden={scrollDirection === "down"}
      isModernDesign={isModernDesign}
      isVibrantDesign={isVibrantDesign}
    >
      <Stack alignItems="flex-start" minWidth={0}>
        <OrderTypeMenu />
        <StoreRating />
      </Stack>
      <BrandLogo logoUrl={logoUrl} />
      <HeaderActions />
    </Bar>
  );
}
